import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from '../components/ui';
import { checkNumber, makeCalcRound } from './calc';
import { elapsed, type DrillProps } from './meta';

interface Answered { text: string; answer: string; given: string; ok: boolean; ms: number }

export function CalcDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeCalcRound(level, seed), [level, seed]);
  const started = useRef(performance.now());
  const asked = useRef(performance.now());
  const [index, setIndex] = useState(0);
  const [input, setInput] = useState('');
  const [log, setLog] = useState<Answered[]>([]);
  const [flash, setFlash] = useState<{ ok: boolean; answer: string } | null>(null);
  const [left, setLeft] = useState(1);
  const q = questions[index];

  const submit = (given: string | null) => {
    if (flash) return;
    const ok = given !== null && (q.choices ? given === q.answer : checkNumber(given, q.answer));
    const entry: Answered = { text: q.text, answer: q.answer, given: given ?? '시간 초과', ok, ms: elapsed(asked.current) };
    setFlash({ ok, answer: q.answer });
    window.setTimeout(() => {
      const next = [...log, entry];
      setLog(next);
      setFlash(null);
      setInput('');
      if (index + 1 >= questions.length) {
        const times = next.map((a) => a.ms).sort((a, b) => a - b);
        onDone({
          correct: next.filter((a) => a.ok).length,
          total: next.length,
          duration_ms: elapsed(started.current),
          detail: { seed, median_ms: times[Math.floor(times.length / 2)], wrong: next.filter((a) => !a.ok).map(({ text, answer, given }) => ({ text, answer, given })) },
        });
        return;
      }
      asked.current = performance.now();
      setLeft(1);
      setIndex(index + 1);
    }, ok ? 500 : 1500);
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (flash) return undefined;
    const id = window.setInterval(() => {
      const remain = 1 - (performance.now() - asked.current) / (q.limitSec * 1000);
      setLeft(Math.max(0, remain));
      if (remain <= 0) submitRef.current(null);
    }, 100);
    return () => window.clearInterval(id);
  }, [index, flash, q.limitSec]);

  return (
    <div className={`drill calc-drill ${flash ? (flash.ok ? 'ok' : 'miss') : ''}`}>
      <div className="row-between">
        <span className="drill-progress">{index + 1} / {questions.length}</span>
        <span className="small muted">{Math.ceil(left * q.limitSec)}초</span>
      </div>
      <Bar value={left} tone={left < 0.3 ? 'warn' : 'accent'} />
      <h2 className="calc-q">{q.text}</h2>
      {q.choices ? (
        <div className="choice-list">
          {q.choices.map((c) => (
            <button
              key={c} type="button" disabled={!!flash} onClick={() => submit(c)}
              className={`choice ${flash && c === q.answer ? 'right' : ''}`}
            >
              {c}
            </button>
          ))}
        </div>
      ) : (
        <form className="calc-form" onSubmit={(e) => { e.preventDefault(); submit(input); }}>
          <input
            key={index} className="input calc-input" inputMode="numeric" autoComplete="off" autoFocus aria-label="답"
            value={input} onChange={(e) => setInput(e.target.value)} disabled={!!flash} enterKeyHint="done"
          />
          <button type="submit" className="btn btn-primary" disabled={!!flash || input.trim() === ''}>확인</button>
        </form>
      )}
      <p className="rule-feedback" aria-live="polite">
        {flash ? (flash.ok ? '정답!' : `정답은 ${flash.answer}`) : ' '}
      </p>
    </div>
  );
}
